<template src="../templates/favourites-map.html"></template>

<script setup>

import {ref, onMounted, defineProps, computed, watch, toRefs} from 'vue';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { config } from '../../dottie/config/config.js';

const favMapContainer = ref(null);

const props = defineProps({
    favourites: {
        type: Array,
        required: true,
    },
    showMap: {
        type: Boolean,
        required: true,
    },
})
const { favourites, showMap } = toRefs(props);

let mapObj = null;
let bounds = L.latLngBounds();
const markers = {};
let markersLayer;

const listings = computed(() => {

    return favourites.value.filter((fav) => fav.type === 'Listing');

});

onMounted(() => {
    // Initialize the map
    mapObj = L.map(favMapContainer.value, config.map.options);

    // Add Mapbox tile layer
    const mapboxTiles = L.tileLayer(
        'https://api.mapbox.com/styles/v1/{id}/tiles/{z}/{x}/{y}?access_token=' +
        document.getElementById('favourites-map').getAttribute('data-map-box-token'),
        {
            attribution:
                '© <a href="https://www.mapbox.com/feedback/">Mapbox</a> © <a href="http://www.openstreetmap.org/copyright">OpenStreetMap</a>',
            tileSize: 512,
            zoomOffset: -1,
            id: 'mapbox/streets-v11',
        }
    );

    mapboxTiles.addTo(mapObj);

    markersLayer = L.layerGroup().addTo(mapObj);

    mapObj.scrollWheelZoom.disable();

    renderListingsMarkers();

});

watch([favourites], () => {
    renderListingsMarkers();
});

const renderListingsMarkers = () => {
    markersLayer.clearLayers();

    if (!listings.value.length) {
        return;
    }

    for (let listing of listings.value) {

        if (listing.latitude && listing.longitude) {
            bounds.extend([listing.latitude, listing.longitude]);
        }

        addMarker(listing);

    }

    if (bounds.isValid()) {

        mapObj.fitBounds(bounds);

    }

}

const addMarker = (listing) => {

    let lat = parseFloat(listing.latitude),
        lng = parseFloat(listing.longitude);

    if (!lat || !lng) {
        return;
    }


    const iconSvg = listing.mainCategory.mapIconSvg;
    let iconObject;
    if (iconSvg) {
        iconObject = L.divIcon({
            iconSize: [48, 48],
            iconAnchor: [18, 30],
            popupAnchor: [0, -30],
            className: 'listing-map-icon-wrapper',
            html: `<div>${iconSvg}</div>`,
        });
    } else {
        iconObject = L.icon({
            iconUrl: listing.mapIconPath,
            iconSize: [listing.mapIconWidth, listing.mapIconHeight],
            iconAnchor: [listing.anchorX ? listing.anchorX : 18, listing.anchorY ? listing.anchorY : 48],
            popupAnchor: [listing.popupAnchorX ? listing.popupAnchorX : 0, listing.popupAnchorY ? listing.popupAnchorY : -48],
        });
    }
    const marker = L.marker([lat, lng], {
        icon: iconObject
    }).addTo(markersLayer);

    const link = listing.link;
    const image = listing.imageURL;
    const website = listing.website ? `| <a href=${listing.website} target="_blank">Visit Website</a>` : '';
    const listingName = listing.link ? `<a href=${link}><strong>${listing.title}</strong></a>` : `<strong>${listing.title}</strong>`;
    const location = listing.city ? listing.city : '';
    const summary = listing.summary ? `<p class="summary">${listing.summary}</p>` : '';

    let content = '';

    if (image) {
        content = `
            <div id="listings-map-infowindow" class="flex gap-half">
                <div class="gmap-img"><a href="${link}" style="background-image: url('${image}')"> </a></div>
                <div>
                    <p>${listingName}</p>
                    <p>${location}</p>
                    ${summary}
                </div>
            </div>
        `;
    } else {
        content = `
            <div id="listings-map-infowindow" class="flex gap-half">
                <div>
                    <p>${listingName}</p>
                    <p>${location}</p>
                    ${summary}
                </div>
            </div>
        `;
    }

    marker.bindPopup(content);

    markers[listing.identifier] = marker;

}
</script>
